import { useEffect, useState } from "react";
import { api, formatINR, formatINRShort, formatDate } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader } from "@/components/Shared";
import { FinanceLock } from "@/components/FinanceLock";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, TrendingDown, FileText } from "lucide-react";

export default function Purchases() {
  return (
    <FinanceLock title="Purchases">
      <PurchasesInner />
    </FinanceLock>
  );
}

function PurchasesInner() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");

  const loadList = () => {
    const params = { limit: 200 };
    if (q) params.q = q;
    api.get("/purchases", { params }).then(({ data }) => setRows(data));
  };

  const loadAll = () => {
    loadList();
    api.get("/purchases/summary").then(({ data }) => setSummary(data));
  };
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);
  useLiveRefresh("finance", loadAll);

  return (
    <div>
      <PageHeader subtitle="Purchase Register" title="Vendor Bills" />
      <div className="p-8 space-y-6">
        {summary && (
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Kpi label="Total Purchases" value={formatINRShort(summary.total_value)} exact={formatINR(summary.total_value)} icon={TrendingDown} accent="text-neutral-900" />
            <Kpi label="Taxable Value" value={formatINRShort(summary.total_taxable)} exact={formatINR(summary.total_taxable)} />
            <Kpi label="Bills" value={summary.bill_count?.toLocaleString("en-IN")} icon={FileText} />
          </section>
        )}

        {summary?.by_vendor?.length > 0 && (
          <div className="border border-neutral-200 bg-white">
            <div className="px-5 py-4 border-b border-neutral-200 label-caps">Top Vendors</div>
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 label-caps">
                <tr>
                  <th className="text-left px-4 py-2.5">Vendor</th>
                  <th className="text-right px-4 py-2.5">Bills</th>
                  <th className="text-right px-4 py-2.5">Total Value</th>
                  <th className="text-right px-4 py-2.5">% of Purchases</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_vendor.map((v) => (
                  <tr key={v.vendor} className="border-t border-neutral-100 data-row">
                    <td className="px-4 py-2.5 font-medium">{v.vendor}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{v.bills}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(v.value)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-neutral-500">
                      {((v.value / summary.total_value) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="border border-neutral-200 bg-white">
          <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="label-caps">Bills</div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
                <Input
                  data-testid="purchases-search"
                  placeholder="Voucher, vendor, invoice…"
                  className="pl-8 rounded-sm w-64"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadList()}
                />
              </div>
              <Button variant="outline" onClick={loadList} className="rounded-sm">Apply</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="purchases-table">
              <thead className="bg-neutral-50 label-caps">
                <tr>
                  <th className="text-left px-4 py-2.5">Voucher</th>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Vendor</th>
                  <th className="text-left px-4 py-2.5">Invoice #</th>
                  <th className="text-left px-4 py-2.5">Description</th>
                  <th className="text-right px-4 py-2.5">Taxable</th>
                  <th className="text-right px-4 py-2.5">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-neutral-100 data-row">
                    <td className="px-4 py-2.5 font-mono-num text-xs">{r.voucher_no}</td>
                    <td className="px-4 py-2.5 text-xs text-neutral-600">{formatDate(r.invoice_date)}</td>
                    <td className="px-4 py-2.5">{r.party_name}</td>
                    <td className="px-4 py-2.5 font-mono-num text-xs">{r.invoice_number || "—"}</td>
                    <td className="px-4 py-2.5 text-neutral-600 max-w-xs truncate" title={r.description}>{r.description}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(r.taxable_value)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{formatINR(r.invoice_value)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">No purchase bills yet. Import a Purchase file first.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, exact, icon: Icon, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-5" title={exact}>
      <div className="flex items-start justify-between">
        <div className="label-caps">{label}</div>
        {Icon && <Icon size={14} className="text-neutral-400" />}
      </div>
      <div className={`font-mono-num text-2xl font-bold mt-2 tracking-tight ${accent || "text-neutral-900"}`}>{value}</div>
      {exact && <div className="text-[11px] text-neutral-500 font-mono-num mt-0.5">{exact}</div>}
    </div>
  );
}
